import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Tooltip, Button, Breadcrumbs, Link, Typography, TextField, Grid, Stack, CircularProgress, IconButton,
    Chip
} from '@mui/material';
import { FaPrint, FaFileExport } from "react-icons/fa";
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import moment from "moment";
import { useReactToPrint } from 'react-to-print';
import { getRoomMasterdropdata } from '../../src/Api/PropertyMst/Api';
import { fetchResident } from "../../src/Components/Masters/Billing/Api/Api";
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../lib/axios';
import Autocomplete from '@mui/material/Autocomplete';
import PrintIcon from '@mui/icons-material/Print';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import HomeIcon from '@mui/icons-material/Home';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

const BookingSumReport = () => {
    const [rowData, setRowData] = useState([]);
    const [printData, setPrintData] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState("All");
    const [selectedResident, setSelectedResident] = useState("All");
    const [property, setProperty] = useState([]);
    const [residents, setResidents] = useState([]);
    const [loading, setLoading] = useState(false);
    const today = dayjs();
    let startYear, endYear;

    if (today.month() >= 3) {
        startYear = today.year();
        endYear = today.year() + 1;
    } else {
        startYear = today.year() - 1;
        endYear = today.year();
    }

    const [filters, setFilters] = useState({
        SearchFrom: dayjs(`${startYear}-04-01`).format('YYYY-MM-DD'),
        toDate: dayjs(`${endYear}-03-31`).format('YYYY-MM-DD'),
    });
    const contentRef = useRef(null);
    const navigate = useNavigate();

    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: "Booking Details Report",
        pageStyle: `
        @page {
            size: A4 portrait;
            margin: 1.5cm;
        }
        @media print {
            body { 
                font-family: Arial, sans-serif; 
                font-size: 11px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            .no-print { display: none !important; }
            
            .print-container {
                max-width: 100%;
            }
            .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #635bff;
                padding-bottom: 10px;
            }
            .print-title {
                font-size: 20px;
                font-weight: bold;
                color: #635bff;
                margin: 8px 0;
            }
            .print-subtitle {
                font-size: 13px;
                color: #555;
                margin: 4px 0;
            }
            table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
            }
            th, td {
                border: 1px solid #ccc;
                padding: 6px 8px;
                text-align: left;
            }
            th {
                background-color: #635bff;
                color: white;
                font-weight: bold;
                position: sticky;
                top: 0;
            }
            tr:nth-child(even) {
                background-color: #f8f9fa;
            }
            .total-row {
                background-color: #e8f5e9 !important;
                font-weight: bold;
            }
            .amount-column {
                text-align: right;
                font-weight: 600;
            }
        }
    `,
    });

    const colDefs = useMemo(() => [
        { field: 'PropName', headerName: 'Property', width: 200 },
        { field: 'RoomTypeName', headerName: 'RoomType', width: 130 },
        { field: 'MonthNm', headerName: 'Month', width: 130 },
        { field: 'YearNm', headerName: 'Year', width: 130 },
        {
            field: 'TotalBookins', headerName: 'TotalBooking', width: 100,
            cellRenderer: (params) => {
                const value = params.value || 0;
                let color = 'default';
                if (value > 10) color = 'success';
                else if (value > 5) color = 'primary';
                else if (value > 0) color = 'info';
                else color = 'default';

                return <Chip label={value} color={color} size="small" sx={{ height: 20 }} />;
            },
        },
        {
            field: 'DepositeAmt', headerName: 'Deposit', width: 110,
            cellRenderer: (params) => {
                const value = params.value || 0;
                return <Chip label={value} size="small" sx={{ bgcolor: '#badef7', color: '#0b54a8', fontWeight: 500, height: 20 }} />
            },
        },
        {
            field: 'BedRent', headerName: 'StdRent', width: 110,
            cellRenderer: (params) => {
                const value = params.value || 0;
                return <Chip label={value} size='small' sx={{ bgcolor: '#a7e9a7', color: '#031803', fontWeight: 500, height: 20 }} />
            }
        },
        {
            field: 'BedRent', headerName: 'BedRent', width: 110,
            cellRenderer: (params) => {
                const value = params.value || 0;
                return <Chip label={value} size='small' sx={{ bgcolor: '#d4d2f8', color: "#635bff", fontWeight: 500, height: 20 }} />
            }
        },
        { field: 'DiscAmt', headerName: 'Discount', width: 110 },
        { field: 'TopupAmt', headerName: 'Topup', width: 110 },
        {
            field: 'NetAmt', headerName: 'Amount', width: 110,
            cellRenderer: (params) => {
                const value = params.value || 0;
                return <Chip label={value} size="small" sx={{ bgcolor: '#fde3f7', color: '#6c1774', fontWeight: 500, height: 20, }} />
            },
        },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);

    useEffect(() => {
        fetchBookingData();
    }, [])

    const fetchBookingData = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post(
                'BookingReports/GetBookingReportsDetail',
                {
                    PropId: selectedProperty === "All" ? 1 : selectedProperty,
                    ResidentId: 1,
                    BookDtFrom: filters.SearchFrom,
                    BookUpToDt: filters.toDate,
                    Flag: "BillSumRpt"
                }
            );

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(row => ({
                    ...row,

                    BillCycle: row.BillCycle === "S" ? "Second" : "First",
                    BookSt: row.BookSt === "B" ? "Booked"
                        : row.BookSt === "C" ? "Confirmed"
                            : row.BookSt === "X" ? "Canceled"
                                : row.BookSt === "O" ? "Occupied"
                                    : row.BookSt === "L" ? "Leaved"
                                        : row.BookSt
                }));
                setRowData(formattedData);
                setPrintData(formattedData);
            } else {
                setRowData([]);
                toast.info(response.data.Message)
            }
        } catch (error) {
            setRowData([]);
            toast.error('Failed to fetch booking details');
        } finally {
            setLoading(false);
        }
    };

    const fetchPropertyData = async () => {
        try {
            const userId = sessionStorage.getItem('UserId');
            const response = await getRoomMasterdropdata({
                Flag: '',
                UserId: userId,
            });

            if (response.data.Status === 0) {
                setProperty([{ Id: 'All', Name: "All" }, ...response.data.Data]);
            } else {
                toast.error(response.data.Message || 'Error fetching property data');
            }
        } catch (error) {
            toast.error('Failed to fetch property data');
        }
    };

    const fetchResidentData = async () => {
        try {
            const response = await fetchResident({
                start: 1,
                PageSize: 1,
                SearchText: "drp",
            });

            if (response?.data?.Data) {
                setResidents([{ Id: 'All', Name: "All" }, ...response.data.Data]);
            } else {
                toast.error("No residents data found");
            }
        } catch (error) {
            toast.error("Failed to load residents data");
        }
    };

    const handleSearch = () => {
        fetchBookingData();
    };

    const handleExport = async () => {
        if (!rowData || rowData.length === 0) {
            toast.info("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Booking Details');

        const headers = colDefs.map((col, index) => ({
            header: col.headerName || col.field,
            key: col.field,
            width: col.width ? col.width / 7 : 15,
        }));

        headers.unshift({ header: 'Sr.No', key: 'srNo', width: 8 });

        worksheet.columns = headers;

        worksheet.getRow(1).eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '635BFF' },
            };
            cell.font = {
                bold: true,
                color: { argb: 'FFFFFF' },
            };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        rowData.forEach((row, index) => {
            const rowObj = { srNo: index + 1 };
            colDefs.forEach((col) => {
                rowObj[col.field] = row[col.field] ?? '';
            });
            worksheet.addRow(rowObj);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `BookingDetails_${filters.SearchFrom}_to_${filters.toDate}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        fetchPropertyData();
        fetchResidentData();
    }, []);

    const handlePropertyChange = (event, value) => {
        const newValue = value ? value.Id : "All";
        setSelectedProperty(newValue);
    };

    const handleResidentChange = (event, value) => {
        const newValue = value ? value.Id : "All";
        setSelectedResident(newValue);
    };

    return (
        <Box sx={{ maxWidth: "100vw", overflowX: "hidden", backgroundColor: "#f9f9f9", minHeight: "90vh" }}>
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "12px 16px", sm: "16px 20px" },
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "90%",
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: { xs: 1, md: 0.5 }, width: '100%' }}
                >
                    {/* Breadcrumbs */}
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        separator="›"
                        sx={{ flexGrow: 1 }}
                    >
                        <Link
                            underline="hover"
                            color="#635bff"
                            sx={{ cursor: "pointer", fontWeight: 500, display: 'flex', alignItems: 'center' }}
                            onClick={() => navigate('/dashboard')}
                        >
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            Home
                        </Link>
                        <Link
                            underline="hover"
                            color="#635bff"
                            sx={{ cursor: "pointer", fontWeight: 500 }}
                            onClick={() => navigate('/reports/bookdetails')}
                        >
                            Booking Report
                        </Link>
                        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                            Booking Summary
                        </Typography>
                    </Breadcrumbs>

                    {/* Filters + Buttons */}
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        alignItems="center"
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="From Date"
                                value={filters.SearchFrom ? dayjs(filters.SearchFrom) : null}
                                onChange={(newValue) =>
                                    handleFilterChange('SearchFrom', newValue ? newValue.format('YYYY-MM-DD') : '')
                                }
                                maxDate={filters.toDate ? dayjs(filters.toDate) : undefined}
                                format="DD/MM/YYYY"
                                sx={{ width: 150 }}
                            />
                            <DatePicker
                                label="To Date"
                                value={filters.toDate ? dayjs(filters.toDate) : null}
                                onChange={(newValue) =>
                                    handleFilterChange('toDate', newValue ? newValue.format('YYYY-MM-DD') : '')
                                }
                                minDate={filters.SearchFrom ? dayjs(filters.SearchFrom) : undefined}
                                format="DD/MM/YYYY"
                                sx={{ width: 150 }}
                            />
                        </LocalizationProvider>

                        <CustomAutocomplete
                            options={property}
                            value={property.find((option) => option.Id === selectedProperty) || null}
                            onChange={handlePropertyChange}
                            id="property-selector"
                            name="Properties"
                            label="Select Property"
                            getOptionLabel={(option) => option.Name || ''}
                            sx={{ width: 170 }}
                        />

                        {/* Buttons */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="Search" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    startIcon={<SearchIcon />}
                                    sx={{
                                        bgcolor: '#635bff',
                                        color: 'white',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        px: 2,
                                        '&:hover': { bgcolor: '#635bff' },
                                    }}
                                >
                                    Search
                                </Button>
                            </Tooltip>
                            {/* <Tooltip title="Print" arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    sx={{
                                        borderColor: 'secondary.main',
                                        color: 'secondary.main',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        px: 2,
                                        '&:hover': { borderColor: 'secondary.dark' },
                                    }}
                                >
                                    Print
                                </Button>
                            </Tooltip> */}
                            <Tooltip title="Export" arrow>
                                <Button
                                    variant="outlined"
                                    startIcon={<FaFileExport />}
                                    onClick={handleExport}
                                    sx={{
                                        borderColor: 'success.main',
                                        color: 'success.main',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        px: 2,
                                        '&:hover': { borderColor: 'success.dark' },
                                    }}
                                >
                                    Export
                                </Button>
                            </Tooltip>
                            <Tooltip title="Previous Page" arrow>
                                <IconButton
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        color: '#ffffff',
                                        bgcolor: '#635bff',
                                        ':hover': { backgroundColor: '#635bff' },
                                    }}
                                    onClick={() => navigate('/reports/bookdetails')}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>

            <Box className="table-grid ag-theme-alpine"
                sx={{
                    height: 'calc(100vh - 240px)',
                    width: '90%',
                    margin: '0 auto',
                    position: 'relative',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}
            >
                {loading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        paginationPageSizeSelector={[100, 500, 1000, 5000]}
                        enableCellTextSelection={true}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        tooltipShowDelay={0}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
                        })}
                    />
                )}
            </Box>

            {
                rowData.length === 0 && (
                    <Box sx={{
                        width: "90%",
                        margin: "20px auto",
                        textAlign: "center",
                        color: "red"
                    }}>
                        No Record Found...
                    </Box>
                )
            }
        </Box >
    );
};

const CustomAutocomplete = ({ options, value, onChange, id, name, label, variant = 'outlined', className = '', getOptionLabel, isOptionEqualToValue }) => {
    return (
        <Autocomplete
            options={options}
            value={value}
            onChange={onChange}
            id={id}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant={variant}
                    className={className}
                    size="small"
                    fullWidth
                />
            )}
            sx={{ minWidth: 200 }}
        />
    );
};

export default BookingSumReport;
